import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import {
  Bar,
  BarChart as RcBarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart as RcLineChart,
  Pie,
  PieChart as RcPieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import CsvButton from 'components/upb_viewer_beta/copilot/components/CsvButton';
import PlainButton from 'components/upb_viewer_beta/copilot/components/PlainButton';
import SimpleTable from 'components/upb_viewer_beta/copilot/components/SimpleTable';
import { A2UI_VIEW_TYPES } from 'components/upb_viewer_beta/copilot/constants';
import {
  ACCENT_VIVID,
  BG_1,
  BG_2,
  BG_3,
  BORDER_1,
  BORDER_2,
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_SIZE_XS,
  FONT_SIZE_XXS,
  FONT_WEIGHT_SEMIBOLD,
  INFO_COLOR,
  RADIUS_MD,
  RADIUS_SM,
  SPACE_1,
  SPACE_2,
  SPACE_3,
  SUCCESS_COLOR,
  WARNING_COLOR,
} from 'components/upb_viewer_beta/css/betaTokens';
import { marcoPieGreen, marcoPieOrange, marcoPieTeal, marcoPieViolet } from 'lib/css/colors';

/**
 * A2uiSurface — renders the agent-declared UI components (Marco's A2UI port).
 *
 * The agent decides what to show by emitting a ```marco-ui block; we render exactly those
 * components and nothing else. There is no client-side "is this chartable?" guessing — a plain
 * answer ships zero components and renders as prose only. Mirrors machan's a2ui contract.
 *
 * `onAction({ name, context })` handles Button events (send_message / navigate).
 */

const PIE_COLORS = [
  ACCENT_VIVID,
  SUCCESS_COLOR,
  INFO_COLOR,
  WARNING_COLOR,
  marcoPieViolet,
  marcoPieTeal,
  marcoPieOrange,
  marcoPieGreen,
];

const isNum = v => typeof v === 'number' && Number.isFinite(v);

const fmt = v => {
  if (v === null || v === undefined || v === '') return '–';
  if (isNum(v)) {
    return Number.isInteger(v)
      ? v.toLocaleString('en-US')
      : v.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  return String(v);
};

const truncTick = s => {
  const str = String(s);
  return str.length > 12 ? `${str.slice(0, 11)}…` : str;
};

// Compact Y-axis ticks so big numbers fit the narrow panel: 1,800,000 -> 1.8M.
const compactFmt = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const compact = v => compactFmt.format(v);

const axisTick = { fontSize: 10, fill: FG_3 };
const tipStyle = { fontSize: FONT_SIZE_XS, borderRadius: RADIUS_SM };

// --- CSV export (client-side; the browser saves the file, nothing leaves the page) ---
const csvCell = v => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const slugify = t =>
  (t || 'marco-table')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'marco-table';

function downloadCsv(title, columns, rows) {
  const lines = [(columns || []).map(csvCell).join(',')];
  (rows || []).forEach(r => lines.push((r.cells || []).map(csvCell).join(',')));
  // Lead with a BOM so Excel reads UTF-8 correctly; CRLF line endings for spreadsheet apps.
  const blob = new Blob([`\ufeff${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slugify(title)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Card({ title, children }) {
  return (
    <div className={css(styles.card)}>
      {title && <div className={css(styles.title)}>{title}</div>}
      {children}
    </div>
  );
}
Card.propTypes = { title: PropTypes.string, children: PropTypes.node.isRequired };
Card.defaultProps = { title: null };

function MetricRow({ comp }) {
  return (
    <Card title={comp.title}>
      <div className={css(styles.metrics)}>
        {comp.metrics.map(m => (
          <div key={m.label} className={css(styles.metric)}>
            <div className={css(styles.metricVal)}>{fmt(m.value)}</div>
            <div className={css(styles.metricLab)}>{m.label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// Resolvers shared by every SimpleTable instance so the rendered <tr>/<td> classes are
// byte-identical to the original inline tables (css() combines td + tdNum for numeric cells).
const trClassName = ri => css(ri % 2 ? styles.trAlt : undefined);
const tdClassName = cell => css(styles.td, isNum(cell) && styles.tdNum);

/** Compact, scrollable table used by the chart "List" view (and shares CSV escaping). */
function MiniTable({ columns, rows }) {
  return (
    <SimpleTable
      columns={columns}
      rows={rows}
      wrapClassName={css(styles.tableWrap)}
      tableClassName={css(styles.table)}
      thClassName={css(styles.th)}
      trClassName={trClassName}
      tdClassName={tdClassName}
      fmt={fmt}
    />
  );
}
MiniTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

/**
 * Wraps a chart with a Chart/List toggle so any ranking or distribution can also be read
 * as a full, scrollable list — and exported to CSV from the list view.
 */
function ChartCard({ title, columns, rows, children }) {
  const [view, setView] = useState(A2UI_VIEW_TYPES.CHART);
  return (
    <Card title={null}>
      <div className={css(styles.tableHead)}>
        {title && <div className={css(styles.tableTitle)}>{title}</div>}
        <div className={css(styles.headRight)}>
          <div className={css(styles.seg)}>
            <PlainButton
              className={css(styles.segBtn, view === A2UI_VIEW_TYPES.CHART && styles.segBtnActive)}
              onClick={() => setView(A2UI_VIEW_TYPES.CHART)}
            >
              Chart
            </PlainButton>
            <PlainButton
              className={css(styles.segBtn, view === A2UI_VIEW_TYPES.LIST && styles.segBtnActive)}
              onClick={() => setView(A2UI_VIEW_TYPES.LIST)}
            >
              List
            </PlainButton>
          </div>
          {view === A2UI_VIEW_TYPES.LIST && (
            <CsvButton
              className={css(styles.csvBtn)}
              onClick={() => downloadCsv(title, columns, rows)}
              title="Download as CSV"
            />
          )}
        </div>
      </div>
      {view === A2UI_VIEW_TYPES.CHART ? children : <MiniTable columns={columns} rows={rows} />}
    </Card>
  );
}
ChartCard.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  children: PropTypes.node.isRequired,
};
ChartCard.defaultProps = { title: null };

function BarChart({ comp, expanded }) {
  const rows = (comp.bars || []).map(b => ({ cells: [b.label, b.value] }));
  return (
    <ChartCard title={comp.title} columns={['Name', 'Value']} rows={rows}>
      <ResponsiveContainer width="100%" height={expanded ? 360 : 210}>
        <RcBarChart data={comp.bars} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={BORDER_1} vertical={false} />
          <XAxis
            dataKey="label"
            tick={axisTick}
            tickFormatter={truncTick}
            interval={0}
            angle={-25}
            textAnchor="end"
            height={52}
          />
          <YAxis tick={axisTick} tickFormatter={compact} width={38} />
          <Tooltip formatter={v => fmt(v)} labelStyle={{ color: FG_1 }} contentStyle={tipStyle} />
          <Bar dataKey="value" fill={ACCENT_VIVID} radius={[3, 3, 0, 0]} />
        </RcBarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function PieChart({ comp, expanded }) {
  const slices = comp.slices || [];
  const total = slices.reduce((sum, s) => sum + (Number(s.value) || 0), 0) || 1;
  const rows = slices.map(s => ({
    cells: [s.label, s.value, `${((Number(s.value) / total) * 100).toFixed(1)}%`],
  }));
  return (
    <ChartCard title={comp.title} columns={['Name', 'Value', 'Share']} rows={rows}>
      <ResponsiveContainer width="100%" height={expanded ? 380 : 220}>
        <RcPieChart>
          <Pie
            data={comp.slices}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="48%"
            outerRadius={expanded ? 130 : 66}
            label={false}
          >
            {comp.slices.map((s, i) => (
              <Cell key={s.label} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={v => fmt(v)} contentStyle={tipStyle} />
          <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
        </RcPieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

function LineChart({ comp, expanded }) {
  return (
    <Card title={comp.title}>
      <ResponsiveContainer width="100%" height={expanded ? 360 : 210}>
        <RcLineChart data={comp.points} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={BORDER_1} vertical={false} />
          <XAxis
            dataKey="label"
            tick={axisTick}
            tickFormatter={truncTick}
            interval="preserveStartEnd"
            height={36}
          />
          <YAxis tick={axisTick} tickFormatter={compact} width={38} />
          <Tooltip formatter={v => fmt(v)} labelStyle={{ color: FG_1 }} contentStyle={tipStyle} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={ACCENT_VIVID}
            strokeWidth={2}
            dot={{ r: 2 }}
          />
        </RcLineChart>
      </ResponsiveContainer>
    </Card>
  );
}

// Long tables render this many rows; the rest is one click away (Show all / CSV download).
const MAX_VISIBLE_ROWS = 50;

function DataTable({ comp }) {
  const [showAll, setShowAll] = useState(false);
  const all = comp.rows || [];
  const total = all.length;
  const truncated = total > MAX_VISIBLE_ROWS && !showAll;
  const rows = truncated ? all.slice(0, MAX_VISIBLE_ROWS) : all;
  return (
    <Card title={null}>
      <div className={css(styles.tableHead)}>
        {comp.title && <div className={css(styles.tableTitle)}>{comp.title}</div>}
        <CsvButton
          className={css(styles.csvBtn)}
          onClick={() => downloadCsv(comp.title, comp.columns, all)}
          title={`Download all ${total} rows as CSV`}
        />
      </div>
      <SimpleTable
        columns={comp.columns}
        rows={rows}
        wrapClassName={css(styles.tableWrap)}
        tableClassName={css(styles.table)}
        thClassName={css(styles.th)}
        trClassName={trClassName}
        tdClassName={tdClassName}
        fmt={fmt}
      />
      {total > MAX_VISIBLE_ROWS && (
        <div className={css(styles.tableFoot)}>
          <span>
            {truncated
              ? `Showing ${MAX_VISIBLE_ROWS} of ${total.toLocaleString('en-US')} rows`
              : `Showing all ${total.toLocaleString('en-US')} rows`}
          </span>
          <PlainButton className={css(styles.linkBtn)} onClick={() => setShowAll(s => !s)}>
            {truncated ? 'Show all' : 'Show fewer'}
          </PlainButton>
          <span className={css(styles.footHint)}>· or download the full set as CSV</span>
        </div>
      )}
    </Card>
  );
}

function ComparisonCard({ comp }) {
  return (
    <Card title={comp.title}>
      <div className={css(styles.cmpHead)}>
        <span />
        <span className={css(styles.cmpLab)}>{comp.leftLabel}</span>
        <span className={css(styles.cmpLab)}>{comp.rightLabel}</span>
      </div>
      {comp.metrics.map(m => (
        <div key={m.label} className={css(styles.cmpRow)}>
          <span className={css(styles.cmpMetric)}>{m.label}</span>
          <span className={css(styles.cmpVal)}>{fmt(m.leftValue)}</span>
          <span className={css(styles.cmpVal)}>{fmt(m.rightValue)}</span>
        </div>
      ))}
    </Card>
  );
}

function ActionButton({ comp, onAction }) {
  const event = (comp.action && comp.action.event) || {};
  return (
    <PlainButton
      className={css(styles.actionBtn)}
      onClick={() => onAction && onAction({ name: event.name, context: event.context || {} })}
    >
      {comp.label || 'Continue'}
    </PlainButton>
  );
}

const REGISTRY = {
  MetricRow,
  BarChart,
  PieChart,
  LineChart,
  DataTable,
  ComparisonCard,
  Button: ActionButton,
};

export default function A2uiSurface({ components, onAction, expanded }) {
  if (!components || !components.length) return null;
  return (
    <div className={css(styles.surface)}>
      {components.map((comp, i) => {
        const Comp = REGISTRY[comp && comp.component];
        if (!Comp) return null;
        // eslint-disable-next-line react/no-array-index-key
        return <Comp key={i} comp={comp} onAction={onAction} expanded={expanded} />;
      })}
    </div>
  );
}

A2uiSurface.propTypes = {
  components: PropTypes.arrayOf(PropTypes.object),
  onAction: PropTypes.func,
  expanded: PropTypes.bool,
};
A2uiSurface.defaultProps = { components: null, onAction: null, expanded: false };

const compShape = PropTypes.object.isRequired;
MetricRow.propTypes = { comp: compShape };
BarChart.propTypes = { comp: compShape, expanded: PropTypes.bool };
PieChart.propTypes = { comp: compShape, expanded: PropTypes.bool };
LineChart.propTypes = { comp: compShape, expanded: PropTypes.bool };
DataTable.propTypes = { comp: compShape };
ComparisonCard.propTypes = { comp: compShape };
ActionButton.propTypes = { comp: compShape, onAction: PropTypes.func };
BarChart.defaultProps = { expanded: false };
PieChart.defaultProps = { expanded: false };
LineChart.defaultProps = { expanded: false };
ActionButton.defaultProps = { onAction: null };

const styles = StyleSheet.create({
  surface: { display: 'flex', flexDirection: 'column', gap: SPACE_2, marginTop: SPACE_2 },
  card: {
    background: BG_2,
    border: `1px solid ${BORDER_1}`,
    borderRadius: RADIUS_MD,
    padding: SPACE_2,
  },
  title: {
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_2,
    marginBottom: SPACE_2,
  },
  tableHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: SPACE_2,
    marginBottom: SPACE_2,
  },
  tableTitle: { fontSize: FONT_SIZE_XS, fontWeight: FONT_WEIGHT_SEMIBOLD, color: FG_2 },
  headRight: { display: 'flex', alignItems: 'center', gap: SPACE_2, marginLeft: 'auto' },
  seg: {
    display: 'inline-flex',
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_SM,
    overflow: 'hidden',
  },
  segBtn: {
    background: 'transparent',
    border: 'none',
    color: FG_3,
    cursor: 'pointer',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XXS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    padding: '3px 11px',
    transition: 'background 0.15s, color 0.15s',
    ':hover': { color: FG_1 },
  },
  segBtnActive: { background: ACCENT_VIVID, color: '#fff', ':hover': { color: '#fff' } },
  csvBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: 'transparent',
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_SM,
    color: FG_2,
    cursor: 'pointer',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XXS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    padding: '3px 9px',
    marginLeft: 'auto',
    flexShrink: 0,
    transition: 'color 0.15s, border-color 0.15s, background 0.15s',
    ':hover': { color: ACCENT_VIVID, borderColor: ACCENT_VIVID, background: BG_1 },
  },
  metrics: { display: 'flex', flexWrap: 'wrap', gap: SPACE_3 },
  metric: { flex: '1 1 80px' },
  metricVal: {
    fontSize: 22,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
  },
  metricLab: { fontSize: FONT_SIZE_XXS, color: FG_3, marginTop: 2 },
  tableWrap: { overflowX: 'auto', maxHeight: 280, overflowY: 'auto', borderRadius: RADIUS_SM },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: FONT_SIZE_XS },
  th: {
    position: 'sticky',
    top: 0,
    background: BG_3,
    color: FG_2,
    textAlign: 'left',
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    padding: '6px 8px',
    borderBottom: `1px solid ${BORDER_2}`,
    whiteSpace: 'nowrap',
  },
  td: {
    color: FG_1,
    padding: '5px 8px',
    borderBottom: `1px solid ${BORDER_1}`,
    whiteSpace: 'nowrap',
  },
  tdNum: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  trAlt: { background: BG_1 },
  tableFoot: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    marginTop: SPACE_1,
    fontSize: FONT_SIZE_XXS,
    color: FG_3,
  },
  linkBtn: {
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    color: ACCENT_VIVID,
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XXS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    textDecoration: 'underline',
  },
  footHint: { color: FG_3 },
  cmpHead: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr 1fr',
    gap: SPACE_2,
    paddingBottom: SPACE_1,
    borderBottom: `1px solid ${BORDER_1}`,
  },
  cmpLab: {
    fontSize: FONT_SIZE_XXS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    color: FG_2,
    textAlign: 'right',
  },
  cmpRow: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr 1fr',
    gap: SPACE_2,
    padding: '5px 0',
    borderBottom: `1px solid ${BG_3}`,
    fontSize: FONT_SIZE_XS,
  },
  cmpMetric: { color: FG_3 },
  cmpVal: { color: FG_1, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  actionBtn: {
    background: 'transparent',
    border: `1px solid ${ACCENT_VIVID}`,
    borderRadius: RADIUS_SM,
    color: ACCENT_VIVID,
    cursor: 'pointer',
    fontFamily: FONT_BODY,
    fontSize: FONT_SIZE_XS,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    padding: '6px 12px',
    alignSelf: 'flex-start',
    ':hover': { background: ACCENT_VIVID, color: '#fff' },
  },
});
